import { deleteUser, getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUI } from '../../lib/ui';

import { db } from '../../lib/firebase/index.js';
import { ACHIEVEMENTS, BUILDINGS } from '../../lib/idle/gameConfig';
import { formatCoins } from '../../lib/idle/format';
import { resetIdleGame, useIdle } from '../../lib/idle/idleStore';
import Styles from './Profile.module.css';

const BACKEND = 'https://brassflows-backend.onrender.com';
// Firebase only lets an account be deleted shortly after signing in.
const RECENT_LOGIN_MS = 5 * 60 * 1000;

const formatDate = (date) =>
  date
    ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : 'N/A';

const Profile = () => {
  const navigate = useNavigate();
  const game = useIdle();
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const [profile, setProfile] = useState({
    username: '',
    highScore: 0,
    ranking: 0,
    membershipStatus: 'Basic',
    subscriptionType: null,
    subscriptionId: null,
    subscriptionEndDate: null,
    subscriptionCancelAtPeriodEnd: false,
    memberSince: null,
    totalPlayers: 0,
  });

  const openPricing = useUI((s) => s.openPricing);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/');
        return;
      }
      const load = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (!userDoc.exists()) return;

          const data = userDoc.data() || {};
          const highScore = Number(data.highScore || 0);
          const membershipStatus = data.membershipStatus || 'Basic';

          const usersRef = collection(db, 'users');
          const [higher, total] = await Promise.all([
            getCountFromServer(query(usersRef, where('highScore', '>', highScore))),
            getCountFromServer(usersRef),
          ]);
          const totalPlayers = total.data().count || 0;
          const creation = user.metadata?.creationTime;

          setProfile({
            username: data.username || '',
            highScore,
            ranking: highScore === 0 ? totalPlayers : (higher.data().count || 0) + 1,
            membershipStatus,
            subscriptionType: data.subscriptionType || null,
            subscriptionId: data.subscriptionId || null,
            subscriptionEndDate: data.subscriptionEndDate?.toDate() || null,
            subscriptionCancelAtPeriodEnd: data.subscriptionCancelAtPeriodEnd || false,
            memberSince: creation ? new Date(creation) : null,
            totalPlayers,
          });

          try {
            localStorage.setItem('membershipStatus', membershipStatus);
          } catch (e) {
            // ignore storage errors
          }
        } catch (err) {
          console.error('Failed to load profile data', err);
        } finally {
          setIsLoading(false);
        }
      };
      load();
    });
    return () => unsubscribe();
  }, [navigate]);

  const callSubscription = async (path, onDone) => {
    setIsWorking(true);
    setActionError(null);
    try {
      const response = await fetch(`${BACKEND}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: profile.subscriptionId }),
      });
      const data = await response.json();
      if (response.ok) onDone(data);
      else setActionError(data.error || 'Something went wrong. Please try again.');
    } catch (error) {
      console.error(`Error calling ${path}:`, error);
      setActionError('Something went wrong. Please try again.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleCancel = () => {
    if (!profile.subscriptionId) return;
    const ok = window.confirm(
      `Cancel your subscription? You'll keep Premium until ${formatDate(profile.subscriptionEndDate)}, but it won't renew after that.`,
    );
    if (!ok) return;
    callSubscription('cancel-subscription', () =>
      setProfile((prev) => ({ ...prev, subscriptionCancelAtPeriodEnd: true })),
    );
  };

  const handleReactivate = () => {
    if (!profile.subscriptionId) return;
    callSubscription('reactivate-subscription', () =>
      setProfile((prev) => ({ ...prev, subscriptionCancelAtPeriodEnd: false })),
    );
  };

  const handleDeleteAccount = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    setActionError(null);

    // Checked first so nothing is deleted if Firebase would refuse the last step.
    const lastSignIn = user.metadata?.lastSignInTime
      ? new Date(user.metadata.lastSignInTime).getTime()
      : 0;
    if (Date.now() - lastSignIn > RECENT_LOGIN_MS) {
      setActionError('For your security, please sign out, sign back in, and try again.');
      return;
    }

    setIsWorking(true);
    try {
      await deleteDoc(doc(db, 'users', user.uid));
      await deleteUser(user);
      resetIdleGame();
      try {
        localStorage.removeItem('membershipStatus');
      } catch (e) {
        // ignore storage errors
      }
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      setActionError(
        error.code === 'auth/requires-recent-login'
          ? 'For your security, please sign out, sign back in, and try again.'
          : 'We could not delete your account. Please try again.',
      );
      setIsWorking(false);
    }
  };

  const {
    username,
    highScore,
    ranking,
    membershipStatus,
    subscriptionType,
    subscriptionEndDate,
    subscriptionCancelAtPeriodEnd,
    memberSince,
    totalPlayers,
  } = profile;

  const isPremium = membershipStatus === 'Premium';
  const hasSubscription = isPremium && ['month', 'year'].includes(subscriptionType);

  const attempts = game.correct + game.wrong;
  const hired = BUILDINGS.reduce((n, b) => n + (game.owned[b.id] || 0), 0);
  const earned = ACHIEVEMENTS.filter((a) => a.test(game)).length;

  const stats = [
    ['High score', highScore.toLocaleString()],
    ['Ranking', ranking === 0 ? 'None' : `#${ranking} of ${totalPlayers}`],
    ['Member since', formatDate(memberSince)],
    ['Coins in the jar', formatCoins(game.coins)],
    ['Gems in the pouch', formatCoins(game.gems)],
    ['Cards answered right', game.correct.toLocaleString()],
    ['Accuracy', attempts ? `${Math.round((game.correct / attempts) * 100)}%` : '—'],
    ['Scale sessions finished', game.scaleSessions.toLocaleString()],
    ['Helpers hired', hired.toLocaleString()],
  ];

  if (isLoading) {
    return (
      <main className={Styles.page}>
        <div className={Styles.column}>
          <div className={`${Styles.sheet} ${Styles.loading}`}>Loading your profile…</div>
        </div>
      </main>
    );
  }

  return (
    <main className={Styles.page}>
      <div className={Styles.column}>
        <header className={Styles.header}>
          <div className={Styles.avatar} aria-hidden="true">
            {(username || 'P').charAt(0).toUpperCase()}
          </div>
          <h1 className={Styles.username}>{username || 'Player'}</h1>
          <span className={`${Styles.badge} ${isPremium ? Styles.badgePremium : ''}`}>
            {membershipStatus}
          </span>
        </header>

        <section className={Styles.sheet}>
          <h2 className={Styles.sectionTitle}>Stats</h2>
          {stats.map(([label, value]) => (
            <div key={label} className={Styles.statRow}>
              <span className={Styles.statLabel}>{label}</span>
              <span className={Styles.statDots} />
              <span className={Styles.statValue}>{value}</span>
            </div>
          ))}
          <div className={Styles.statRow}>
            <span className={Styles.statLabel}>Achievements</span>
            <span className={Styles.statDots} />
            <Link to="/achievements" className={Styles.statLink}>
              {earned} / {ACHIEVEMENTS.length}
            </Link>
          </div>
        </section>

        <section className={Styles.sheet}>
          <h2 className={Styles.sectionTitle}>Membership</h2>

          {actionError && (
            <p className={Styles.error} role="alert">
              {actionError}
            </p>
          )}

          {isPremium ? (
            <>
              {hasSubscription && (
                <div className={Styles.statRow}>
                  <span className={Styles.statLabel}>Plan</span>
                  <span className={Styles.statDots} />
                  <span className={Styles.statValue}>
                    {subscriptionType === 'month' ? 'Monthly' : 'Annual'}
                  </span>
                </div>
              )}
              {subscriptionEndDate && (
                <div className={Styles.statRow}>
                  <span className={Styles.statLabel}>
                    {subscriptionCancelAtPeriodEnd ? 'Access until' : 'Next renewal'}
                  </span>
                  <span className={Styles.statDots} />
                  <span className={Styles.statValue}>{formatDate(subscriptionEndDate)}</span>
                </div>
              )}
              {subscriptionCancelAtPeriodEnd && (
                <p className={Styles.notice}>
                  Your subscription is set to cancel and will not renew.
                </p>
              )}
              <div className={Styles.actions}>
                {subscriptionCancelAtPeriodEnd ? (
                  <button
                    type="button"
                    className={`${Styles.btn} ${Styles.btnGo}`}
                    onClick={handleReactivate}
                    disabled={isWorking}
                  >
                    {isWorking ? 'Working…' : 'Keep my subscription'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${Styles.btn} ${Styles.btnDanger}`}
                    onClick={handleCancel}
                    disabled={isWorking || !profile.subscriptionId}
                  >
                    {isWorking ? 'Working…' : 'Cancel subscription'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className={Styles.sub}>
                Premium unlocks every helper, custom ranges and more.
              </p>
              <div className={Styles.actions}>
                <button
                  type="button"
                  className={`${Styles.btn} ${Styles.btnGo}`}
                  onClick={() => openPricing('Premium keeps your band playing while you are away.')}
                >
                  Upgrade to Premium
                </button>
              </div>

              <div className={Styles.dangerZone}>
                <h3 className={Styles.dangerTitle}>Delete account</h3>
                <p className={Styles.sub}>
                  Permanently removes your account and saved progress. This can&apos;t be undone.
                </p>
                {confirmingDelete ? (
                  <div className={Styles.confirm}>
                    <label className={Styles.confirmLabel} htmlFor="confirmDelete">
                      Type DELETE to confirm
                    </label>
                    <input
                      id="confirmDelete"
                      className={Styles.input}
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      autoComplete="off"
                    />
                    <div className={Styles.actions}>
                      <button
                        type="button"
                        className={Styles.btn}
                        onClick={() => {
                          setConfirmingDelete(false);
                          setConfirmText('');
                        }}
                        disabled={isWorking}
                      >
                        Keep my account
                      </button>
                      <button
                        type="button"
                        className={`${Styles.btn} ${Styles.btnDanger}`}
                        onClick={handleDeleteAccount}
                        disabled={isWorking || confirmText !== 'DELETE'}
                      >
                        {isWorking ? 'Deleting…' : 'Delete forever'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={Styles.actions}>
                    <button
                      type="button"
                      className={`${Styles.btn} ${Styles.btnDanger}`}
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Delete account
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default Profile;